import { useCallback, useMemo } from "react";
import { useFieldsContext } from "context/FieldsProvider";
import {
  fieldCropName,
  fieldVarietyName,
  userFullName,
} from "utils/namingUtils";
import { useTranslation } from "react-i18next";

const perFarmer = (fields) => {
  const perFarmer = {};
  fields.forEach((field) => {
    if (field.farmerDetails?.length) {
      field.farmerDetails.forEach((farmer) => {
        const associatedFarmerRegistered = farmer.email in perFarmer;
        if (associatedFarmerRegistered) {
          perFarmer[farmer.email].fields.push(field);
        } else {
          perFarmer[farmer.email] = {
            fields: [field],
            name: userFullName({ user: farmer }),
          };
        }
      });
    } else {
      const agronomistFarmer = field.agronomistDetails?.[0];
      if (!agronomistFarmer) {
        return;
      }
      const associatedFarmerRegistered = agronomistFarmer.email in perFarmer;
      if (associatedFarmerRegistered) {
        perFarmer[agronomistFarmer.email].fields.push(field);
      } else {
        perFarmer[agronomistFarmer.email] = {
          fields: [field],
          name: userFullName({ user: agronomistFarmer }),
        };
      }
    }
    return;
  });

  return perFarmer;
};

const perAgronomist = (fields) => {
  const perAgronomist = {};
  fields.forEach((field) => {
    if (field.agronomistDetails?.length) {
      field.agronomistDetails.forEach((agronomist) => {
        const associatedAgronomistRegistered =
          agronomist.email in perAgronomist;
        if (associatedAgronomistRegistered) {
          perAgronomist[agronomist.email].fields.push(field);
        } else {
          perAgronomist[agronomist.email] = {
            fields: [field],
            name: userFullName({ user: agronomist }),
          };
        }
      });
    } else {
      const farmerAgronomist = field.farmerDetails?.[0];
      if (!farmerAgronomist) {
        return;
      }
      const associatedAgronomistRegistered =
        farmerAgronomist.email in perAgronomist;
      if (associatedAgronomistRegistered) {
        perAgronomist[farmerAgronomist.email].fields.push(field);
      } else {
        perAgronomist[farmerAgronomist.email] = {
          fields: [field],
          name: userFullName({ user: farmerAgronomist }),
        };
      }
    }
    return;
  });

  return perAgronomist;
};

const perVariety = (fields, isEn) => {
  const perVariety = {};
  fields.forEach((field) => {
    const variety = field.varietyId;
    if (variety in perVariety) {
      perVariety[variety].fields.push(field);
    } else {
      // check because there is broken data with not varietyRead
      if (variety) {
        return;
      }
      perVariety[variety] = {
        fields: [field],
        name: fieldVarietyName({ field, isEn }),
      };
    }
  });

  return perVariety;
};

const perCrop = (fields, isEn) => {
  const perCrop = {};
  fields.forEach((field) => {
    const crop = field.varietyRead?.cropId;
    if (crop in perCrop) {
      const variety = field.varietyId;
      if ("varieties" in perCrop[crop] && variety in perCrop[crop].varieties) {
        perCrop[crop].varieties[variety].fields.push(field);
      } else {
        // check because there is broken data with not varietyRead
        if (!variety) {
          return;
        }
        perCrop[crop].varieties[variety] = {
          fields: [field],
          name: fieldVarietyName({ field, isEn }),
        };
      }
    } else {
      // check because there is broken data with not varietyRead
      if (!crop) {
        return;
      }
      perCrop[crop] = {
        name: fieldCropName({ field, isEn }),
        varieties: {
          [field.varietyId]: {
            fields: [field],
            name: fieldVarietyName({ field, isEn }),
          },
        },
      };
    }
  });

  return perCrop;
};

const getFieldsPer = () => {};
getFieldsPer.farmer = (fields) => perFarmer(fields);
getFieldsPer.agronomist = (fields) => perAgronomist(fields);
getFieldsPer.variety = (fields, isEn) => perVariety(fields, isEn);
getFieldsPer.crop = (fields, isEn) => perCrop(fields, isEn);

export const useGroupFieldsPer = () => {
  const { i18n } = useTranslation();
  const isEn = useMemo(() => i18n.language.startsWith("en"), [i18n.language]);

  const { fields } = useFieldsContext();

  const getFieldsPerFarmer = useCallback(
    () => getFieldsPer.farmer(fields),
    [fields]
  );
  const getFieldsPerAgronomist = useCallback(
    () => getFieldsPer.agronomist(fields),
    [fields]
  );
  const getFieldsPerVariety = useCallback(
    () => getFieldsPer.variety(fields, isEn),
    [fields, isEn]
  );
  const getFieldsPerCrop = useCallback(
    () => getFieldsPer.crop(fields, isEn),
    [fields, isEn]
  );

  return {
    getFieldsPerFarmer,
    getFieldsPerAgronomist,
    getFieldsPerVariety,
    getFieldsPerCrop,
  };
};
